import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
} from 'reactstrap';
import classnames from 'classnames';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import ConfirmModal from '../../../../Components/crude/ConfirmModal';
import ActionButton from '../../../../Components/Common/ActionButton';
import { DetailBackButton } from '../../../../Components/Common/Detail';
import ProcurementRequirementView from './tabs/ProcurementRequirementView';
import ProcurementSupportingDocumentTab from './tabs/ProcurementSupportingDocumentTab';
import ProcurementDmutView from './tabs/ProcurementDmutView';
import ProcurementValidityTab from './tabs/ProcurementValidityTab';
import ProcurementOverviewTab from './tabs/ProcurementOverviewTab';
import * as api from './procurementApi';
import { isAllCompleted } from './procurementValidation';
import {
  PROC_STATUS,
  PROC_STATUS_BADGE,
  STEP,
  STEP_STATUS,
} from './procurementConstants';

// The five tabs. Only Supporting Document (2) and Validity (4) are editable and
// own a step; Main Requirement (1) and DMUT (3) are read-only echoes from
// Planning whose steps the server auto-completes. Submit lives on Overview (5).
const TABS = [
  { id: 1, icon: 'ri-list-ordered', label: 'MAIN REQUIREMENT', step: null },
  {
    id: 2,
    icon: 'ri-file-text-line',
    label: 'SUPPORTING DOCUMENT',
    step: STEP.SUPPORTING_DOCUMENT,
  },
  {
    id: 3,
    icon: 'ri-mail-send-line',
    label: 'REGISTERED SUPPLIER INVITATION',
    step: null,
  },
  // Tab VALIDITY dihapus (2026-09-14, desain acuan): jadwal Submission
  // Deadline + Validity Period kini kartu di ATAS tab Main Requirement dengan
  // tombol simpan sendiri (ProcurementValidityTab dirender di TabPane 1).
  { id: 4, icon: 'ri-checkbox-circle-line', label: 'OVERVIEW', step: null },
];

const OVERVIEW_TAB = 4;

// Procurement Edit (Draft) / Detail (Submitted, read-only) share this shell.
// `readOnly` is forced on for submitted records regardless of the route used.
const ProcurementForm = ({ readOnly: readOnlyProp = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState(1);
  const [submitModal, setSubmitModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const loadDetail = useCallback(async () => {
    try {
      const { record: rec, detail: det } = await api.getProcurement(id);
      setRecord(rec);
      setDetail(det);
    } catch (error) {
      toast.error(`Failed to load procurement: ${error}`);
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // The Supporting Document tab mutates server state immediately (add/delete/
  // toggle documents); it calls this to pull fresh server truth for just the
  // collection it owns, without clobbering unsaved edits on other tabs.
  const refresh = useCallback(async () => {
    const { detail: det } = await api.getProcurement(id);
    return det;
  }, [id]);

  const readOnly = readOnlyProp || record?.status === PROC_STATUS.SUBMITTED;

  const allCompleted = useMemo(
    () => (detail ? isAllCompleted(detail.steps) : false),
    [detail]
  );

  document.title = record ? `Procurement - ${record.procId}` : 'Procurement';

  const currentStep = TABS.find(t => t.id === activeTab)?.step;

  // ---- Per-tab persistence -------------------------------------------------
  // Supporting Document and Validity each save their own step. `completed`
  // decides InProgress (Save as Draft) vs Completed (Save & Completed); the
  // latter runs the client-side check first, then the server enforces the same.
  const saveActiveTab = async completed => {
    if (!currentStep) return;
    const stepStatus = completed
      ? STEP_STATUS.COMPLETED
      : STEP_STATUS.IN_PROGRESS;

    setSaving(true);
    try {
      if (currentStep === STEP.SUPPORTING_DOCUMENT) {
        // Completed requires >=1 Requirement document (server-enforced).
        if (completed && (detail.requirementDocuments || []).length === 0) {
          toast.error(
            'At least one Requirement document is needed to complete Supporting Document.'
          );
          return;
        }
        await api.saveDocumentStatus(id, stepStatus);
      }
      // Step VALIDITY tidak lagi disimpan dari sini — kartu jadwal di tab Main
      // Requirement menyimpan dirinya sendiri (tombol "Simpan Perubahan Jadwal").
      await loadDetail();
      toast.success(completed ? 'Saved & marked completed' : 'Saved as draft');
    } catch (error) {
      toast.error(`Save failed: ${error}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await api.submitProcurement(id);
      setSubmitModal(false);
      toast.success('Procurement submitted');
      navigate('/crude/procurement');
    } catch (error) {
      toast.error(`Submit failed: ${error}`);
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => setActiveTab(t => Math.max(1, t - 1));

  if (notFound) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5'>
            <i className='ri-error-warning-line fs-36 text-muted'></i>
            <h5 className='mt-2'>Procurement not found</h5>
            <ActionButton
              variant='secondary'
              size='sm'
              className='mt-2'
              icon='ri-arrow-left-line'
              onClick={() => navigate('/crude/procurement')}
            >
              Back to list
            </ActionButton>
          </div>
        </Container>
      </div>
    );
  }

  if (!record || !detail) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5 text-muted'>Loading...</div>
        </Container>
      </div>
    );
  }

  const isTabDone = tabId => tabId < activeTab;

  // Floating-button visibility: Save buttons on the two editable tabs; Submit on
  // Overview once every step is Completed.
  const showBack = activeTab > 1;
  const showSaveButtons = !readOnly && !!currentStep;
  const showSubmit = !readOnly && activeTab === OVERVIEW_TAB && allCompleted;

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                {/* Header mengikuti gaya Planning (/crude/planning): breadcrumb
                    box + tombol back bundar + judul bold + meta ber-ikon. */}
                <Row className='mt-n2 mb-1 mb-sm-0'>
                  <div className='col-sm order-3 order-sm-2'>
                    <div className='hstack gap-sm-1 align-items-center flex-wrap email-topbar-link'>
                      <h5 className='card-title mb-0 flex-grow-1'>
                        <Breadcrumb listClassName='p-3 py-2 bg-light'>
                          <BreadcrumbItem>
                            <Link to='/dashboard'>Home</Link>
                          </BreadcrumbItem>
                          <BreadcrumbItem>
                            <Link to='/crude/procurement'>Procurement</Link>
                          </BreadcrumbItem>
                          <BreadcrumbItem active>
                            Detail #{record.procId || '-'}
                          </BreadcrumbItem>
                        </Breadcrumb>
                      </h5>
                    </div>
                  </div>
                </Row>

                <Row className='mb-4'>
                  <Col xs={12}>
                    <Row className='align-items-center g-3'>
                      <Col xs={12} lg>
                        <div className='dt-header__top'>
                          <DetailBackButton
                            label='Back to list'
                            onClick={() => navigate('/crude/procurement')}
                          />
                          <Badge
                            color={
                              PROC_STATUS_BADGE[record.status] || 'secondary'
                            }
                            className='fw-semibold px-3 py-1 rounded-pill'
                          >
                            {record.status}
                          </Badge>
                        </div>
                        <div className='d-flex align-items-start gap-3'>
                          <div className='flex-grow-1'>
                            <h4 className='mb-1 fw-bold text-dark'>
                              {record.procurementType} — {record.title || '-'}
                            </h4>
                            <div className='d-flex flex-wrap align-items-center gap-2 text-muted small'>
                              <i className='ri-hashtag'></i>
                              <span>
                                Procurement ID: {record.procId || '-'}
                              </span>
                              <span className='d-none d-md-inline text-secondary opacity-25'>
                                |
                              </span>
                              <span>{record.procurementMethod}</span>
                            </div>
                          </div>
                          {/* Route dtl selalu read-only — beri lompatan sekali
                              klik ke mode edit selama masih Draft. */}
                          {readOnlyProp &&
                            record.status === PROC_STATUS.DRAFT && (
                              <ActionButton
                                variant='primary'
                                icon='ri-pencil-line'
                                onClick={() =>
                                  navigate(`/crude/procurement/create/${id}`)
                                }
                              >
                                Edit Procurement
                              </ActionButton>
                            )}
                        </div>
                      </Col>
                    </Row>
                    <hr className='mt-3 mb-0 border-secondary opacity-10' />
                  </Col>
                </Row>

                <Row className='justify-content-center mb-4'>
                  <Col xxl={12}>
                    <CardBody className='checkout-tab'>
                      <div className='mx-n4 px-4 message-list-scroll'>
                        <Nav
                          tabs
                          className='nav-tabs-custom nav-primary mt-1 mb-3'
                        >
                          {TABS.map(tab => (
                            <NavItem key={tab.id}>
                              <NavLink
                                className={classnames('cursor-pointer', {
                                  active: activeTab === tab.id,
                                  done: isTabDone(tab.id),
                                })}
                                onClick={() => setActiveTab(tab.id)}
                              >
                                <i
                                  className={`${tab.icon} align-middle me-1`}
                                ></i>
                                <span className='fw-semibold'>{tab.label}</span>
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>
                      </div>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId={1}>
                          {activeTab === 1 && (
                            <>
                              {/* Kartu jadwal (eks tab VALIDITY) — simpan
                                  sendiri via "Simpan Perubahan Jadwal". */}
                              <ProcurementValidityTab
                                procurementValidityId={id}
                                detail={detail}
                                setDetail={setDetail}
                                readOnly={readOnly}
                              />
                              <ProcurementRequirementView detail={detail} />
                            </>
                          )}
                        </TabPane>
                        <TabPane tabId={2}>
                          {activeTab === 2 && (
                            <ProcurementSupportingDocumentTab
                              procurementValidityId={id}
                              planningId={record.planningId}
                              detail={detail}
                              setDetail={setDetail}
                              refresh={refresh}
                              readOnly={readOnly}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId={3}>
                          {activeTab === 3 && (
                            <ProcurementDmutView
                              procurementValidityId={id}
                              detail={detail}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId={4}>
                          {activeTab === 4 && (
                            <ProcurementOverviewTab detail={detail} />
                          )}
                        </TabPane>
                      </TabContent>

                      {/* Floating action bar */}
                      {(showBack || showSaveButtons || showSubmit) && (
                        <div
                          className='d-flex gap-2 justify-content-end'
                          style={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 5,
                            padding: '12px 4px',
                            marginTop: '1rem',
                            background:
                              'linear-gradient(to top, rgba(255,255,255,0.95) 60%, rgba(255,255,255,0))',
                          }}
                        >
                          {showBack && (
                            <ActionButton
                              variant='secondary'
                              icon='ri-arrow-left-line'
                              onClick={goBack}
                            >
                              Back
                            </ActionButton>
                          )}
                          {showSaveButtons && (
                            <>
                              <ActionButton
                                variant='secondary'
                                icon='ri-draft-line'
                                disabled={saving}
                                onClick={() => saveActiveTab(false)}
                              >
                                Save as Draft
                              </ActionButton>
                              <ActionButton
                                variant='primary'
                                icon='ri-check-double-line'
                                loading={saving}
                                onClick={() => saveActiveTab(true)}
                              >
                                Save &amp; Completed
                              </ActionButton>
                            </>
                          )}
                          {showSubmit && (
                            <ActionButton
                              variant='primary'
                              icon='ri-send-plane-line'
                              loading={saving}
                              onClick={() => setSubmitModal(true)}
                            >
                              Submit
                            </ActionButton>
                          )}
                        </div>
                      )}

                      {!readOnly &&
                        activeTab === OVERVIEW_TAB &&
                        !allCompleted && (
                          <div className='text-end text-muted fst-italic mt-2'>
                            Complete Supporting Document and Validity (Save
                            &amp; Completed) to enable Submit.
                          </div>
                        )}
                    </CardBody>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Container>

        <ConfirmModal
          show={submitModal}
          title='Submit Procurement'
          message='Once submitted, this procurement advances to Tender Opening and can no longer be edited. Continue?'
          confirmLabel='Submit'
          confirmColor='primary'
          onConfirm={handleSubmit}
          onClose={() => setSubmitModal(false)}
        />
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </React.Fragment>
  );
};

export default ProcurementForm;
